import * as React from 'react';
import { cn } from '@/lib/utils';

const QuestionSidebarContainer = React.forwardRef(({ className, children, ...props }, ref) => (
  <aside
    ref={ref}
    className={cn('c-question-sidebar-container', 'text-[13px]', 'gap-4', 'flex', 'flex-col', 'w-full', '[&_p]:mb-0', className)}
    {...props}
  >
    {children}
  </aside>
));
QuestionSidebarContainer.displayName = 'QuestionSidebarContainer';

const QuestionSidebarBody = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-body', 'flex', 'flex-col', 'gap-4', 'w-full', className)}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarBody.displayName = 'QuestionSidebarBody';

const QuestionSidebarSection = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-section', 'bg-white', 'rounded-[10px]', 'p-6', 'border', 'border-solid', 'border-neutral-lightest', 'shadow-lg/5', 'w-full', className)}
    style={{
      boxShadow: '0px 4px 10px rgba(68, 43, 140, 0.05)'
    }}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarSection.displayName = 'QuestionSidebarSection';

const QuestionSidebarTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-question-sidebar-title',
      'text-primary-dark',
      'font-extrabold',
      'mb-5',
      'text-base',
      // Estilos aninhados para o span
      '[&_span]:font-light',
      '[&_span]:text-[13px]',
      '[&_span]:text-neutral',
      // Classe para sobrescrever font-size se estiver dentro de __yesterday
      '[&.c-question-sidebar-yesterday]:text-sm',
      className
    )}
    {...props}
  >
    {children}
  </h3>
));
QuestionSidebarTitle.displayName = 'QuestionSidebarTitle';

const QuestionSidebarHighlight = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-highlight', 'text-neutral-darker', 'bg-neutral-lightest', 'rounded-md', 'p-4', 'border', 'border-solid', 'border-neutral-lighter', 'shadow-lg/5', className)}
    style={{
      boxShadow: '0px 4px 10px rgba(68, 43, 140, 0.05)'
    }}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarHighlight.displayName = 'QuestionSidebarHighlight';

const QuestionSidebarTime = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-time', 'flex items-center justify-between', 'flex-wrap', 'gap-2', 'mb-4', className)}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarTime.displayName = 'QuestionSidebarTime';

const QuestionSidebarClock = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-question-sidebar-clock',
      'text-base',
      'font-extrabold',
      'text-primary',
      'flex items-center justify-center',
      'flex-wrap',
      // Estilos aninhados para o SVG
      '[&_svg]:h-5',
      '[&_svg]:w-5',
      '[&_svg]:mr-[.3em]',
      className
    )}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarClock.displayName = 'QuestionSidebarClock';

const QuestionSidebarChart = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-chart', 'mb-4', 'flex items-center', className)}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarChart.displayName = 'QuestionSidebarChart';

const QuestionSidebarChartContainer = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-question-sidebar-chart-container',
      // Estilos aninhados para Highcharts (seletor de classes externo)
      '[&_.highcharts-container]:!w-20', // 80px
      '[&_.highcharts-container]:!h-20', // 80px
      '[&_.highcharts-root]:!w-20',
      '[&_.highcharts-root]:!h-20',
      'flex-shrink-0', // Evita que encolha
      className
    )}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarChartContainer.displayName = 'QuestionSidebarChartContainer';

const QuestionSidebarLegends = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-legends', 'flex-grow', className)}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarLegends.displayName = 'QuestionSidebarLegends';

const QuestionSidebarLegend = React.forwardRef(({ className, children, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-question-sidebar-legend', 'text-neutral-darker', 'mb-0', className)}
    {...props}
  >
    {children}
  </p>
));
QuestionSidebarLegend.displayName = 'QuestionSidebarLegend';

const QuestionSidebarCount = React.forwardRef(({ className, variant, children, ...props }, ref) => {
  const variantClass = variant === 'success' ? 'text-success' : variant === 'error' ? 'text-danger' : '';

  return (
    <span
      ref={ref}
      className={cn('c-question-sidebar-count', 'font-extrabold', variantClass, className)}
      {...props}
    >
      {children}
    </span>
  );
});
QuestionSidebarCount.displayName = 'QuestionSidebarCount';

const QuestionSidebarYesterday = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-question-sidebar-yesterday',
      'bg-white',
      'rounded-xl',
      'p-3',
      'text-neutral',
      'mb-6',
      'shadow-lg/5',
      // Estilos aninhados
      '[&_strong]:text-primary-dark',
      '[&_p]:mb-0',
      className
    )}
    style={{
      boxShadow: '0px 4px 10px rgba(68, 43, 140, 0.05)'
    }}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarYesterday.displayName = 'QuestionSidebarYesterday';

const QuestionSidebarHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-sidebar-header', 'w-full', className)}
    {...props}
  >
    {children}
  </div>
));
QuestionSidebarHeader.displayName = 'QuestionSidebarHeader';

export {
  QuestionSidebarContainer,
  QuestionSidebarBody,
  QuestionSidebarSection,
  QuestionSidebarTitle,
  QuestionSidebarHighlight,
  QuestionSidebarTime,
  QuestionSidebarClock,
  QuestionSidebarChart,
  QuestionSidebarChartContainer,
  QuestionSidebarLegends,
  QuestionSidebarLegend,
  QuestionSidebarCount,
  QuestionSidebarYesterday,
  QuestionSidebarHeader
};
